// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/ConfiguracoesWhatsApp.tsx
//  Credenciais Z-API + botão "Testar Conexão".
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, CheckCircle2, XCircle, Loader2, MessageSquare } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { useWhatsAppConfig } from '@/features/whatsapp/hooks/useWhatsApp';
import { getApiErrorMessage } from '@/lib/apiClient';

export default function ConfiguracoesWhatsApp() {
  const { query, salvar, testarConexao } = useWhatsAppConfig();

  const [baseUrl,     setBaseUrl]     = useState('https://api.z-api.io');
  const [instanceId,  setInstanceId]  = useState('');
  const [token,       setToken]       = useState('');
  const [clientToken, setClientToken] = useState('');
  const [isAtivo,     setIsAtivo]     = useState(false);
  const [tokenFoiDigitado,       setTokenFoiDigitado]       = useState(false);
  const [clientTokenFoiDigitado, setClientTokenFoiDigitado] = useState(false);

  useEffect(() => {
    const cfg = query.data;
    if (!cfg) return;
    setBaseUrl(cfg.baseUrl ?? 'https://api.z-api.io');
    setInstanceId(cfg.instanceId ?? '');
    setIsAtivo(cfg.isAtivo);
    // Token/ClientToken não vêm completos do back — só substituímos se o usuário digitar de novo
    setToken('');
    setClientToken('');
    setTokenFoiDigitado(false);
    setClientTokenFoiDigitado(false);
  }, [query.data]);

  const handleSalvar = async () => {
    try {
      await salvar.mutateAsync({
        baseUrl,
        instanceId: instanceId.trim(),
        token:       tokenFoiDigitado       ? token.trim()       : undefined,
        clientToken: clientTokenFoiDigitado ? clientToken.trim() : undefined,
        isAtivo,
      });
      toast.success('Configurações salvas.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const handleTestar = async () => {
    try {
      const r = await testarConexao.mutateAsync();
      if (r.conectado) toast.success(`Conexão OK${r.sessionStatus ? ` (${r.sessionStatus})` : ''}`);
      else toast.error(r.erro ?? 'Falha na conexão.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  if (query.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <MessageSquare className="h-6 w-6 text-green-600" />
        <div>
          <h1 className="text-2xl font-bold">WhatsApp (Z-API)</h1>
          <p className="text-sm text-muted-foreground">
            Credenciais da integração. Obtenha em{' '}
            <a href="https://developer.z-api.io" target="_blank" rel="noreferrer" className="underline">
              developer.z-api.io
            </a>.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credenciais Z-API</CardTitle>
          <CardDescription>
            Deixe os campos de token em branco para preservar o valor já salvo.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1 md:col-span-2">
            <Label>Base URL</Label>
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.z-api.io"
            />
          </div>

          <div className="space-y-1">
            <Label>Instance ID *</Label>
            <Input
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value)}
              placeholder="3D1F0E2A..."
            />
          </div>

          <div className="space-y-1">
            <Label>
              Token *{query.data?.tokenResumido && (
                <span className="ml-2 text-xs font-mono text-muted-foreground">
                  (atual: {query.data.tokenResumido})
                </span>
              )}
            </Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => { setToken(e.target.value); setTokenFoiDigitado(true); }}
              placeholder={query.data?.tokenResumido ? 'deixe em branco para manter' : 'token da instância'}
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <Label>
              Client-Token (opcional)
              {query.data?.temClientToken && (
                <span className="ml-2 text-xs text-muted-foreground">(já salvo)</span>
              )}
            </Label>
            <Input
              type="password"
              value={clientToken}
              onChange={(e) => { setClientToken(e.target.value); setClientTokenFoiDigitado(true); }}
              placeholder={query.data?.temClientToken ? 'deixe em branco para manter' : 'se sua instância exigir'}
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between border-t pt-4">
            <div>
              <p className="text-sm font-medium">Ativa</p>
              <p className="text-xs text-muted-foreground">
                Quando desligada, todas as tentativas de envio falham silenciosamente (grava log).
              </p>
            </div>
            <Switch checked={isAtivo} onCheckedChange={setIsAtivo} />
          </div>
        </CardContent>
      </Card>

      {testarConexao.data && (
        <Alert variant={testarConexao.data.conectado ? 'default' : 'destructive'}>
          {testarConexao.data.conectado
            ? <CheckCircle2 className="h-4 w-4" />
            : <XCircle className="h-4 w-4" />}
          <AlertTitle>
            {testarConexao.data.conectado ? 'Conectado' : 'Não conectado'}
          </AlertTitle>
          <AlertDescription>
            {testarConexao.data.erro
              ?? testarConexao.data.sessionStatus
              ?? 'Sem detalhes adicionais.'}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          onClick={handleTestar}
          disabled={!query.data?.isAtivo || testarConexao.isPending}
          className="gap-2"
        >
          {testarConexao.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Testar conexão
        </Button>
        <Button onClick={handleSalvar} disabled={salvar.isPending} className="gap-2">
          <Save className="h-4 w-4" />
          {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
}
